
import { useState, useEffect, memo, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  className?: string;
  priority?: boolean;
  fallbackSrc?: string;
  onLoad?: () => void;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

const OptimizedImage = memo(({
  src,
  alt,
  width,
  height,
  className = '',
  priority = false,
  fallbackSrc = '/placeholder.svg',
  onLoad,
  objectFit = 'cover'
}: OptimizedImageProps) => {
  const [imgSrc, setImgSrc] = useState<string>(priority ? src : fallbackSrc);
  const [isLoading, setIsLoading] = useState<boolean>(!priority);
  const [error, setError] = useState<boolean>(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Choose appropriate resolution based on device
  const getOptimizedSrc = (originalSrc: string): string => {
    // Skip optimization for SVGs, data URLs, or already processed URLs
    if (!originalSrc || 
        originalSrc.startsWith('data:') || 
        originalSrc.includes('?w=') || 
        originalSrc.endsWith('.svg')) {
      return originalSrc;
    }
    
    // For remote images, add a width parameter if not already present
    if (originalSrc.startsWith('http')) {
      const screenWidth = window.innerWidth;
      const appropriateWidth = screenWidth < 768 ? 480 : 
                             screenWidth < 1200 ? 720 : 1080;
                             
      // Only add parameter if URL doesn't already have query params
      if (!originalSrc.includes('?')) {
        return `${originalSrc}?w=${appropriateWidth}&q=80`;
      }
    }
    
    return originalSrc;
  };

  useEffect(() => {
    if (!src) {
      setImgSrc(fallbackSrc);
      setIsLoading(false);
      return;
    }

    if (priority) {
      setImgSrc(getOptimizedSrc(src));
      setIsLoading(false);
      return;
    }
    
    // Use intersection observer to only load when near viewport
    const element = imgRef.current;
    if (!element) return;

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const optimizedSource = getOptimizedSrc(src);
          
          const img = new Image();
          img.src = optimizedSource;
          
          img.onload = () => {
            setImgSrc(optimizedSource);
            setIsLoading(false);
            observerRef.current?.disconnect();
            onLoad?.();
          };
          
          img.onerror = () => {
            console.error(`Failed to load image: ${optimizedSource}`);
            setImgSrc(fallbackSrc);
            setIsLoading(false);
            setError(true);
            observerRef.current?.disconnect();
          };
        }
      });
    }, {
      rootMargin: '200px 0px', // Start loading images 200px before they enter viewport
      threshold: 0.01
    });
    
    observerRef.current.observe(element);
    
    return () => {
      observerRef.current?.disconnect();
    };
  }, [src, priority, fallbackSrc, onLoad]);

  // Create srcset for responsive images if width is provided
  const getSrcSet = () => {
    if (!src || error) return undefined;
    
    // Only create srcset for remote images (not for local assets)
    if (src.startsWith('http')) {
      const baseSrc = src.split('?')[0];
      return `${baseSrc}?w=480&q=75 480w, ${baseSrc}?w=720&q=75 720w, ${baseSrc}?w=1080&q=75 1080w`;
    }
    
    return undefined;
  };

  return (
    <div className={className} style={{ position: 'relative', width: width ? `${width}px` : '100%', height: height ? `${height}px` : '100%' }}>
      {isLoading && (
        <Skeleton 
          className="absolute inset-0 z-10"
          aria-hidden="true"
        />
      )}
      <img
        ref={imgRef}
        src={imgSrc}
        alt={alt}
        width={width}
        height={height}
        className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300 w-full h-full`}
        style={{ objectFit }}
        loading={priority ? "eager" : "lazy"}
        srcSet={getSrcSet()}
        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        onError={() => {
          if (imgSrc !== fallbackSrc) {
            console.error(`Image error, falling back: ${imgSrc}`);
            setImgSrc(fallbackSrc);
            setError(true);
          }
        }}
        onLoad={() => {
          setIsLoading(false);
          onLoad?.();
        }}
      />
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

export default OptimizedImage;
